import React from "react";
import type { Dispatch, SetStateAction } from "react";
import { Box, Avatar, CircularProgress, IconButton, Tooltip } from "@mui/joy";
import { PhotoCameraIcon } from "../../../styles/StyledMUI";
import { AppConfigContext } from "../../../contexts/app/appConfig";
import { useAlert } from "../../../contexts/app/notificationToast";
import {
  PHOTO_MAX_BYTES,
  PHOTO_MAX_EDGE,
  PHOTO_QUALITY,
  resizeImageToFile,
} from "../../../utils/fileSaver";
import {
  colorTokens,
  ctaAccentColor,
  ctaAccentHoverColor,
  textTokens,
} from "../../../styles/globalStyles";

import empty from "../../../styles/images/noimage.png";

type PhotoComponentProps = {
  photo: string | File | undefined;
  setPhoto: Dispatch<SetStateAction<string | File | undefined>>;
};

export default function PhotoComponent(props: PhotoComponentProps) {
  const [previewUrl, setPreviewUrl] = React.useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();

  React.useEffect(() => {
    if (props.photo instanceof File) {
      const url = URL.createObjectURL(props.photo);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    const raw = (props.photo || "").toString().trim();
    if (!raw) {
      setPreviewUrl(undefined);
    } else if (raw.startsWith("http") || raw.startsWith("data:")) {
      setPreviewUrl(raw);
    } else {
      setPreviewUrl((appConfig?.zekayAPI || "") + raw);
    }
    return () => {};
  }, [props.photo, appConfig?.zekayAPI]);

  const handlePick = () => fileInputRef.current?.click();

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) {
      try {
        const resized = await resizeImageToFile(
          file,
          PHOTO_MAX_EDGE,
          PHOTO_MAX_EDGE,
          PHOTO_QUALITY,
          PHOTO_MAX_BYTES,
        );
        props.setPhoto(resized);
      } catch {
        showAlert("Impossible de compresser cette photo.", "warning");
      }
    }
    setIsLoading(false);
  };

  return (
    <Box sx={{ padding: 1, position: "relative", textAlign: "center" }}>
      <Box
        sx={{
          position: "relative",
          width: 120,
          height: 120,
          margin: "auto",
          borderRadius: "50%",
          overflow: "hidden",
          border: `2px solid ${colorTokens.border.default}`,
          backgroundColor: colorTokens.bg.surface,
        }}
      >
        <Avatar
          src={previewUrl || empty}
          alt="photo"
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        <input
          ref={fileInputRef}
          style={{ display: "none" }}
          type="file"
          accept="image/*"
          onChange={handleUpload}
        />
        <Tooltip title="Changer la photo">
          <IconButton
            onClick={handlePick}
            aria-label="Changer la photo"
            size="md"
            sx={{
              position: "absolute",
              bottom: 6,
              right: 6,
              zIndex: 2,
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: ctaAccentColor,
              color: textTokens.color.inverse,
              boxShadow: "0 2px 6px rgba(0,0,0,0.6)",
              "&:hover": { bgcolor: ctaAccentHoverColor },
            }}
          >
            {!isLoading ? <PhotoCameraIcon /> : <CircularProgress size="sm" />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
